import './instrument';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider, toastBridge } from '@/components/ui/Toast';
import { formatApiError } from '@/lib/api-errors';
import { ConfirmProvider } from '@/components/ui/ConfirmProvider';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { installChunkRecovery, isChunkLoadError } from '@/lib/chunk-recovery';
import App from './App';
import i18n, { i18nReady } from './i18n';
import './index.css';

// Avant tout rendu : un onglet ouvert avant le dernier déploiement doit se
// recharger tout seul, pas afficher une page morte.
installChunkRecovery();

const queryClient = new QueryClient({
  // Filet de sécurité : toute mutation qui échoue le dit à l'utilisateur,
  // même quand l'écran n'a prévu aucun retour (c'était le cas de tout un
  // module : 49 mutations muettes). Les gestionnaires locaux (`onError`
  // d'un hook, `catch` d'un `mutateAsync`) s'exécutent dans le même tour ;
  // on laisse passer un tick et on ne double pas un toast déjà affiché.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.silent) return;
      const at = Date.now();
      setTimeout(() => {
        if (toastBridge.lastErrorAt >= at) return;
        const { title, detail } = formatApiError(error);
        toastBridge.show('error', title, detail);
      }, 0);
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // Garde les listes déjà visitées 10 min en cache : retour arrière
      // instantané (affichage immédiat + refetch en arrière-plan si périmé).
      gcTime: 10 * 60_000,
    },
  },
});

// Le premier rendu attend la langue détectée : sinon l'interface apparaîtrait
// un instant avec des clés de traduction brutes.
void i18nReady.then(() => {
  render();
});

function render() {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ErrorBoundary
        fallback={(err) => {
          // On n'arrive ici, sur un morceau manquant, que si le rechargement
          // automatique a déjà été tenté : inutile de montrer le nom du fichier,
          // il ne dit rien à l'utilisateur. On nomme la cause réelle.
          const stale = isChunkLoadError(err);
          return (
            <div className="flex min-h-screen items-center justify-center p-6 text-center">
              <div>
                <div className="text-lg font-semibold text-danger">
                  {stale ? i18n.t('errorBoundary.appUpdated') : i18n.t('errorBoundary.generic')}
                </div>
                <div className="mt-1 text-sm text-muted">
                  {stale ? i18n.t('errorBoundary.appUpdatedHint') : err.message}
                </div>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="mt-4 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white"
                >
                  {i18n.t('errorBoundary.reload')}
                </button>
              </div>
            </div>
          );
        }}
      >
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ToastProvider>
              <ConfirmProvider>
                <App />
              </ConfirmProvider>
            </ToastProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}
